import React, { useMemo, useState } from "react";
import { Input, PageTitle, PageWrapper, Spinner } from "@cinesys/ui/components";
import { Search } from "react-feather";
import styled from "styled-components";
import { useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import { developerToolsEnabled } from "../../services/env";

/**
 * Complete reimagining (Addison: "I don't think a table is the best way to parse this
 * information") of the iconik platform balance-per-org screen — same single query
 * (`GET /api/config/organization/billing`), no new fields fetched. The actual admin task here is
 * "who's in the red, and by how much" — so the page leads with that and compresses everything
 * that doesn't need a look into a dense strip instead of full-size rows.
 *
 * LOW_BALANCE_THRESHOLD (round 2, Addison): a resolved balance under $1,000 is still shown in
 * green (it isn't a deficit) but is grouped into "Needs attention" rather than "In good
 * standing" — a heads-up before an org actually goes negative, not an alarm. A failed lookup
 * ("Unknown" / a raw iconik error string) is its own third section — it isn't a balance problem,
 * it's a "we couldn't check" problem, and conflating the two made both harder to act on.
 * "cinesys" orgs (internal/sandbox, never real customers) are filtered out — but only in prod;
 * dev/staging (`env.developerToolsEnabled`) keeps them visible so they're usable for testing.
 */

interface Billing {
  systemDomainId: string;
  title: string;
  balance: string;
}

interface ParsedBilling extends Billing {
  amount: number | null;
}

const LOW_BALANCE_THRESHOLD = 1000;

const Caption = styled.small`
  color: var(--gray100);
  font-size: 13px;
  margin-left: 12px;
`;

const Eyebrow = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray100);
  margin-bottom: 12px;
`;

const SearchWrap = styled.div`
  max-width: 320px;
  margin-bottom: 22px;
`;

const StatStrip = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  background-color: var(--dark100);
  border: 1px solid var(--dark300);
  border-radius: 10px;
  padding: 12px 18px;
  min-width: 140px;
`;

const StatCount = styled.div<{ $tone: string }>`
  font-size: 28px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  color: var(--${(p) => p.$tone}400);
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--gray100);
  margin-top: 2px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const AllClear = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--green400);
  font-size: 14px;
  background-color: var(--greenBackground);
  border: 1px solid var(--greenBorder);
  border-radius: 10px;
  padding: 14px 16px;
`;

const EmptyNote = styled.div`
  color: var(--gray100);
  font-size: 13px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 12px;
`;

const Card = styled.div<{ $tone: string }>`
  background-color: var(--dark110);
  border: 1px solid var(--dark300);
  border-left: 3px solid var(--${(p) => p.$tone}400);
  border-radius: 8px;
  padding: 14px 16px;
`;

const OrgName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--gray400);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 8px;
`;

const BalanceFigure = styled.div<{ $tone: string }>`
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--${(p) => p.$tone}400);
`;

const UnavailableNote = styled.div`
  font-size: 13px;
  color: var(--orange400);
`;

const GoodGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const GoodPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--dark100);
  border: 1px solid var(--dark300);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
`;

const GoodName = styled.span`
  color: var(--gray300);
`;

const GoodBalance = styled.span`
  color: var(--green300);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

/** The backend always formats a resolved balance as `Intl.NumberFormat` currency text, e.g.
 *  `"-$45.00 (USD)"` — always starting with an optional "-" then "$". Anything else ("Unknown",
 *  or a raw `JSON.stringify`'d iconik error) means the lookup failed, not a real figure. */
function parseBalance(balance: string): number | null {
  const match = /^(-?)\$([\d,]+\.\d{2})/.exec(balance);
  if (!match) {
    return null;
  }
  const amount = parseFloat(match[2].replace(/,/g, ""));
  return match[1] === "-" ? -amount : amount;
}

const byAmountAsc = (a: ParsedBilling, b: ParsedBilling) =>
  (a.amount ?? 0) - (b.amount ?? 0);
const byTitle = (a: ParsedBilling, b: ParsedBilling) =>
  a.title.localeCompare(b.title);

const BillingListScreen = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["billingList"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: Billing[] }>(
        "GET",
        `/api/config/organization/billing`
      );
      return response.data;
    },
  });

  const [search, setSearch] = useState("");
  const isSearching = search.trim().length > 0;

  const { needsAttention, unavailable, goodStanding } = useMemo(() => {
    const query = search.trim().toLowerCase();
    const parsed: ParsedBilling[] = (data ?? [])
      .filter(
        (organization) =>
          developerToolsEnabled ||
          !organization.title.toLowerCase().includes("cinesys")
      )
      .filter((organization) =>
        organization.title.toLowerCase().includes(query)
      )
      .map((organization) => ({
        ...organization,
        amount: parseBalance(organization.balance),
      }));

    return {
      needsAttention: parsed
        .filter((o) => o.amount !== null && o.amount < LOW_BALANCE_THRESHOLD)
        .sort(byAmountAsc),
      unavailable: parsed.filter((o) => o.amount === null).sort(byTitle),
      goodStanding: parsed
        .filter((o) => o.amount !== null && o.amount >= LOW_BALANCE_THRESHOLD)
        .sort(byAmountAsc),
    };
  }, [data, search]);

  return (
    <div>
      <PageTitle
        title="Billing"
        error={isError ? "Problem loading billing" : undefined}
        titleChildren={<Caption>Iconik platform balance per org</Caption>}
      ></PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <SearchWrap>
              <Input
                value={search}
                onChange={setSearch}
                placeholder="Search organizations"
                icon={Search}
                clearable
              />
            </SearchWrap>

            <StatStrip>
              <StatChip>
                <StatCount $tone="gray">
                  {needsAttention.length +
                    unavailable.length +
                    goodStanding.length}
                </StatCount>
                <StatLabel>Organizations</StatLabel>
              </StatChip>
              <StatChip>
                <StatCount $tone={needsAttention.length > 0 ? "red" : "green"}>
                  {needsAttention.length}
                </StatCount>
                <StatLabel>Need attention</StatLabel>
              </StatChip>
              <StatChip>
                <StatCount $tone="green">{goodStanding.length}</StatCount>
                <StatLabel>In good standing</StatLabel>
              </StatChip>
            </StatStrip>

            {!(isSearching && needsAttention.length === 0) && (
              <Section>
                <Eyebrow>Needs attention</Eyebrow>
                {needsAttention.length === 0 ? (
                  <AllClear>
                    Every organization is at ${LOW_BALANCE_THRESHOLD} or above.
                  </AllClear>
                ) : (
                  <CardGrid>
                    {needsAttention.map((org) => (
                      <Card
                        key={org.systemDomainId}
                        $tone={org.amount! < 0 ? "red" : "green"}
                      >
                        <OrgName title={org.title}>{org.title}</OrgName>
                        <BalanceFigure
                          $tone={org.amount! < 0 ? "red" : "green"}
                        >
                          {org.balance}
                        </BalanceFigure>
                      </Card>
                    ))}
                  </CardGrid>
                )}
              </Section>
            )}

            {goodStanding.length > 0 && (
              <Section>
                <Eyebrow>In good standing</Eyebrow>
                <GoodGrid>
                  {goodStanding.map((org) => (
                    <GoodPill key={org.systemDomainId}>
                      <GoodName>{org.title}</GoodName>
                      <GoodBalance>{org.balance}</GoodBalance>
                    </GoodPill>
                  ))}
                </GoodGrid>
              </Section>
            )}

            {!(isSearching && unavailable.length === 0) && (
              <Section>
                <Eyebrow>Balance unavailable</Eyebrow>
                {unavailable.length === 0 ? (
                  <EmptyNote>Every balance resolved successfully.</EmptyNote>
                ) : (
                  <CardGrid>
                    {unavailable.map((org) => (
                      <Card key={org.systemDomainId} $tone="orange">
                        <OrgName title={org.title}>{org.title}</OrgName>
                        <UnavailableNote>
                          Couldn&rsquo;t look up balance
                        </UnavailableNote>
                      </Card>
                    ))}
                  </CardGrid>
                )}
              </Section>
            )}
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default BillingListScreen;
